(() => {
  'use strict';

  const CARD_SELECTOR = '[data-enhanced-pricing-card]';
  const PRICING_JSON_SELECTOR = '[data-enhanced-pricing-json]';
  const VARIANTS_JSON_SELECTOR = '[data-enhanced-pricing-variants]';
  const VARIANT_OPTIONS_JSON_SELECTOR = '[data-enhanced-pricing-variant-options]';

  const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const getQuantity = (form) => {
    const multiFlavorQuantity = parseInt(form?.dataset?.camoMultiFlavorActualQuantity || '', 10);
    if (Number.isFinite(multiFlavorQuantity) && multiFlavorQuantity > 0) return multiFlavorQuantity;

    const input = form?.querySelector('input[name="quantity"]');
    const quantity = parseInt(input?.value || '1', 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const getVariantId = (form) => {
    const input = form?.querySelector('input[name="id"], select[name="id"]');
    return input?.value ? String(input.value) : '';
  };

  const formatMoney = (value, currency) => {
    const number = toNumber(value);
    if (number === null) return '';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(number);
    } catch (error) {
      return `$${number.toFixed(2)}`;
    }
  };

  const parseJson = (element, fallback) => {
    if (!element) return fallback;
    try {
      return JSON.parse(element.textContent || '');
    } catch (error) {
      return fallback;
    }
  };

  const normalizeGroups = (data) => {
    if (!data) return [];
    if (Array.isArray(data.groups)) return data.groups;
    if (Array.isArray(data.products)) return data.products;
    if (Array.isArray(data)) return data;
    return [];
  };

  const normalizeOptionValue = (value) => String(value || '')
    .trim()
    .toLowerCase()
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ');

  const normalizeOptionKey = (value) => normalizeOptionValue(value)
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  const variantMatchesGroup = (group, variantId) => {
    const ids = group.variant_ids || group.variantIds || [];
    return ids.map(String).includes(String(variantId));
  };

  const getVariantOptionMap = (variantOptions, variantId) => {
    const variant = variantOptions?.[String(variantId)];
    const map = {};
    if (!variant) return map;

    const optionNames = Array.isArray(variant.option_names) ? variant.option_names : [];
    optionNames.forEach((name, index) => {
      const key = normalizeOptionKey(name);
      map[key] = variant[`option${index + 1}`];
    });

    return map;
  };

  const findGroup = (groups, variantId, variantOptions) => {
    const directMatch = groups.find((group) => variantMatchesGroup(group, variantId));
    if (directMatch) return directMatch;

    // Flavor products create new variant IDs, but the pricing metafield still maps
    // to the existing Type + Pack Type pair. Fall back to those two option values
    // so flavor can exist without hiding the product-page purchase summary.
    const optionMap = getVariantOptionMap(variantOptions, variantId);
    const selectedType = normalizeOptionValue(optionMap.type);
    const selectedPackType = normalizeOptionValue(optionMap['pack-type'] || optionMap.pack);

    if (selectedType || selectedPackType) {
      const optionMatch = groups.find((group) => {
        const groupType = normalizeOptionValue(group.product_type);
        const groupPackType = normalizeOptionValue(group.pack_type);
        return (!groupType || groupType === selectedType) && (!groupPackType || groupPackType === selectedPackType);
      });

      if (optionMatch) return optionMatch;
    }

    return groups.find((group) => !group.variant_ids && !group.variantIds) || null;
  };

  const getSortedBreakpoints = (group) => {
    const breakpoints = Array.isArray(group?.breakpoints) ? group.breakpoints : [];
    return breakpoints
      .map((breakpoint) => ({
        ...breakpoint,
        quantity: Number(breakpoint.quantity),
        discount_percentage: Number(
          breakpoint.discount_percentage ?? breakpoint.discountPercentage ?? breakpoint.discount ?? 0
        )
      }))
      .filter((breakpoint) => Number.isFinite(breakpoint.quantity) && breakpoint.quantity > 0)
      .sort((a, b) => a.quantity - b.quantity);
  };

  // Uses the highest eligible discount breakpoint at or below the selected quantity.
  // Example: quantity 13 pouches uses the 12-pouch discount percentage.
  const findActiveBreakpoint = (group, quantity) => {
    const breakpoints = getSortedBreakpoints(group);
    if (!breakpoints.length) return null;

    let active = { quantity: 1, discount_percentage: 0 };
    for (const breakpoint of breakpoints) {
      if (breakpoint.quantity <= quantity) active = breakpoint;
      if (breakpoint.quantity > quantity) break;
    }
    return active;
  };

  const buildVariantPriceMap = (variants) => {
    const map = new Map();
    if (!Array.isArray(variants)) return map;

    variants.forEach((variant) => {
      const id = String(variant.id || '');
      const cents = toNumber(variant.price);
      if (!id || cents === null) return;
      map.set(id, cents / 100);
    });

    return map;
  };

  const calculatePrices = ({ baseUnitPrice, quantity, pouchCountPerUnit, discountPercentage }) => {
    const discountMultiplier = Math.max(0, 1 - ((toNumber(discountPercentage) || 0) / 100));
    const total = baseUnitPrice * quantity * discountMultiplier;
    const perPouch = total / Math.max(1, quantity * pouchCountPerUnit);

    return {
      total: Math.round((total + Number.EPSILON) * 100) / 100,
      perPouch: Math.round((perPouch + Number.EPSILON) * 100) / 100
    };
  };

  const initCard = (card) => {
    const pricingJsonEl = card.querySelector(PRICING_JSON_SELECTOR);
    const variantsJsonEl = card.querySelector(VARIANTS_JSON_SELECTOR);
    const variantOptionsJsonEl = card.querySelector(VARIANT_OPTIONS_JSON_SELECTOR);
    const summary = card.querySelector('[data-enhanced-pricing-summary]');
    const totalEl = card.querySelector('[data-enhanced-pricing-total]');
    const perPouchEl = card.querySelector('[data-enhanced-pricing-per-pouch]');
    const form = card.closest('form');
    const content = card.closest('.xo-product-info-content');

    if (!pricingJsonEl || !summary || !totalEl || !perPouchEl || !form) return;

    const data = parseJson(pricingJsonEl, null);
    const variantPriceMap = buildVariantPriceMap(parseJson(variantsJsonEl, []));
    const variantOptions = parseJson(variantOptionsJsonEl, {});
    const groups = normalizeGroups(data);
    if (!groups.length) return;

    let lastState = '';

    const hideSummary = () => {
      summary.hidden = true;
      content?.classList.remove('xo-enhanced-pricing--ready');
    };

    const update = () => {
      const variantId = getVariantId(form);
      const quantity = getQuantity(form);
      const group = findGroup(groups, variantId, variantOptions);
      const breakpoint = findActiveBreakpoint(group, quantity);
      const baseUnitPrice = variantPriceMap.get(String(variantId));

      if (!group || !breakpoint || !baseUnitPrice) {
        hideSummary();
        return;
      }

      const pouchCountPerUnit = Math.max(1, toNumber(group.pouch_count_per_unit) || 1);
      const currency = group.currency || data.currency || 'USD';
      const prices = calculatePrices({
        baseUnitPrice,
        quantity,
        pouchCountPerUnit,
        discountPercentage: breakpoint.discount_percentage
      });

      totalEl.textContent = formatMoney(prices.total, currency);
      perPouchEl.textContent = `${formatMoney(prices.perPouch, currency)} per pouch`;
      summary.hidden = false;
      content?.classList.add('xo-enhanced-pricing--ready');
      lastState = `${variantId}:${quantity}`;
    };

    const scheduleUpdate = () => window.requestAnimationFrame(update);
    const scheduleSteppedUpdate = () => {
      [0, 50, 150, 300].forEach((delay) => window.setTimeout(update, delay));
    };

    form.addEventListener('change', scheduleUpdate);
    form.addEventListener('input', scheduleUpdate);
    form.addEventListener('camo:multi-flavor-quantity-change', scheduleSteppedUpdate);
    form.addEventListener('click', scheduleSteppedUpdate);

    document.addEventListener('change', (event) => {
      if (event.target && event.target.closest('xo-product')) scheduleSteppedUpdate();
    });

    document.addEventListener('click', (event) => {
      if (event.target && event.target.closest('xo-product')) scheduleSteppedUpdate();
    });

    const idInput = form.querySelector('input[name="id"], select[name="id"]');
    if (idInput) {
      new MutationObserver(scheduleUpdate).observe(idInput, { attributes: true, attributeFilter: ['value'] });
    }

    window.setInterval(() => {
      const state = `${getVariantId(form)}:${getQuantity(form)}`;
      if (state !== lastState) update();
    }, 300);

    update();
  };

  const init = () => document.querySelectorAll(CARD_SELECTOR).forEach(initCard);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
